import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Footprints } from 'lucide-react';
import { logSteps } from '@/hooks/useSteps';
import { useUserStore } from '@/store/userStore';
import { useTelegram } from '@/hooks/useTelegram';

/**
 * Шкала: 10 отметок по тысяче. Число выбрано не из головы — столько же стаканов
 * рисует вода (норма 30 мл/кг ≈ 2.4 л ÷ 250 мл = 10), а две соседние строки
 * с разным количеством иконок смотрятся как две случайные полоски, а не как
 * один блок. Заодно шкала до 10 000 даёт запас ВЫШЕ цели: перевыполнение видно,
 * а не упирается в край.
 */
const STEP_UNIT = 1000;
const MARKS = 10;
const GOAL = 7000;

/**
 * Шаги за день — строкой, как вода.
 *
 * ЗАЧЕМ ПОЯВИЛОСЬ: раньше строка с шагами показывалась на главной только при
 * steps > 0, а вносить их можно было единственным способом — уйти в Профиль
 * и найти там поле. Человек, ни разу не вносивший шаги, вообще не видел,
 * что это возможно. Замерено 02.09.2026: 4 записи за неделю на двоих из 43.
 * Фича была, входа в неё не было.
 *
 * Почему ряд следов, а не поле ввода: человек смотрит число в приложении
 * здоровья и переносит его примерно — «где-то шесть тысяч». Ряд читается
 * мгновенно и сам показывает, сколько осталось, а тап ставит значение
 * одним движением. Точность до шага здесь никому не нужна.
 *
 * Тап по следу — поставить столько тысяч (как в воде, «долить до сюда»).
 * Тап по последнему заполненному — снять отметку.
 * Тап по числу справа — точный ввод, если хочется как есть.
 */
export default function StepsCard({
  steps,
  onSaved,
}: {
  steps: number;
  onSaved: () => void;
}) {
  const { profile } = useUserStore();
  const { haptic } = useTelegram();
  const [exact, setExact] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  // Вчера и позавчера: человек часто вспоминает про шаги уже после полуночи.
  // В Профиле такой выбор был всегда, и, вынося шаги на главную, я его
  // потерял — а это единственное место, куда теперь пойдут вносить.
  const [dayOffset, setDayOffset] = useState(0);

  const dateFor = (offset: number) => {
    const d = new Date();
    d.setDate(d.getDate() - offset);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const filled = Math.min(MARKS, Math.floor(steps / STEP_UNIT));
  const done = steps >= GOAL;
  // Свечение по заполнению: на пустой строке его нет — незачем требовать
  // внимания у того, кто сегодня ещё не отмечался.
  const glow = Math.min(1, steps / GOAL);

  async function save(n: number) {
    if (n < 0 || n > 100000) return;
    setSaving(true);
    try {
      await logSteps(n, profile?.weight_kg ?? 70, dayOffset === 0 ? undefined : dateFor(dayOffset));
      onSaved();
      setExact(false);
      setValue('');
      setDayOffset(0);
    } catch (e) {
      console.error('[StepsCard] save error', e);
      haptic('error');
    } finally {
      setSaving(false);
    }
  }

  const tap = (index: number) => {
    // Шкала отражает СЕГОДНЯШНИЕ шаги. Для прошлых дней только точный ввод —
    // иначе тап по следам показывал бы одно, а записывал другое.
    if (dayOffset !== 0) { setExact(true); return; }
    // Тап по последнему заполненному снимает его — отдельная кнопка отмены не нужна.
    if (index + 1 === filled) {
      haptic('medium');
      save(index * STEP_UNIT);
    } else {
      haptic('light');
      save((index + 1) * STEP_UNIT);
    }
  };

  return (
    <div
      className="relative bg-bg-card rounded-xl px-3 py-2.5 mb-4 border transition-colors"
      style={{
        borderColor: done ? 'rgba(111,203,154,0.45)' : 'rgba(34,43,51,1)',
        boxShadow: glow > 0 ? `0 0 ${10 + glow * 14}px rgba(111,203,154,${0.1 + glow * 0.22})` : undefined,
      }}
    >
      <div className="flex items-center gap-2">
        <span className="text-[11px] text-ink-dim shrink-0">Шаги</span>

        <div className="flex-1 flex items-center justify-center gap-[3px]">
          {Array.from({ length: MARKS }).map((_, i) => {
            const isFull = i < filled;
            // Отметки сверх цели — другим цветом: перевыполнение это не то же самое,
            // что выполнение, и сливать их в один цвет значит прятать результат.
            const aboveGoal = i >= GOAL / STEP_UNIT;
            return (
              <motion.button
                key={i}
                onClick={() => tap(i)}
                disabled={saving}
                whileTap={{ scale: 0.82 }}
                aria-label={isFull ? `Снять отметку ${i + 1} тысяч` : `Отметить ${i + 1} тысяч шагов`}
                className="p-0.5 -m-0.5"
              >
                <Footprints
                  className={`w-[15px] h-[15px] transition-colors ${
                    !isFull ? 'text-ink-dim/35' : aboveGoal ? 'text-warn' : 'text-emerald-glow'
                  }`}
                  style={isFull ? {
                    filter: aboveGoal
                      ? 'drop-shadow(0 0 5px rgba(232,168,92,0.75))'
                      : 'drop-shadow(0 0 5px rgba(111,203,154,0.75))',
                  } : undefined}
                  aria-hidden
                />
              </motion.button>
            );
          })}
        </div>

        <button
          onClick={() => { setExact((v) => !v); haptic('light'); }}
          className="text-[11px] tabular-nums shrink-0 text-right leading-tight"
        >
          <span className={done ? 'text-emerald-glow font-semibold' : 'text-ink'}>
            {steps > 0 ? (steps / 1000).toFixed(1) : '0'}
          </span>
          <span className="text-ink-dim">/{(GOAL / 1000).toFixed(0)}к</span>
          <span className="block text-[9px] text-ink-dim">
            {steps > 0 ? 'уточнить' : 'отметить'}
          </span>
        </button>
      </div>

      <AnimatePresence>
        {exact && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="pt-2.5 mt-2.5 border-t border-line">
              <div className="flex gap-1 p-1 bg-bg-elevated rounded-lg mb-2">
                {[0, 1, 2].map((off) => (
                  <button
                    key={off}
                    onClick={() => { setDayOffset(off); haptic('light'); }}
                    className={`flex-1 py-1.5 px-2 rounded-md text-[11px] font-medium transition-all ${
                      dayOffset === off ? 'bg-bg text-ink shadow-sm' : 'text-ink-muted'
                    }`}
                  >
                    {off === 0 ? 'Сегодня' : off === 1 ? 'Вчера' : 'Позавчера'}
                  </button>
                ))}
              </div>
            <div className="flex gap-1.5">
              <input
                type="number"
                inputMode="numeric"
                className="input text-sm flex-1 py-1.5"
                placeholder={dayOffset === 0 ? 'Сколько шагов' : `Шаги за ${dayOffset === 1 ? 'вчера' : 'позавчера'}`}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                autoFocus
              />
              <button
                onClick={() => save(Number(value))}
                disabled={saving || !value}
                className="px-4 rounded-xl bg-emerald-glow text-bg font-bold text-sm disabled:opacity-50"
              >
                {saving ? '…' : 'ОК'}
              </button>
            </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
